// Deadlines bound an operator action; they do not poll or retry Firestore.
// Call assertActive between stages so a timed-out action cannot start later writes.
export function yieldInventoryUi() {
  return new Promise((resolve) => {
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      resolve();
    };
    // Browsers pause animation frames for hidden/suspended tabs. Painting must
    // never be a prerequisite for securing inventory in Firestore.
    const timer = setTimeout(finish, 32);
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(finish);
  });
}

export function withInventoryDeadline(operation, label = "Inventory operation", timeoutMs = 60000) {
  let active = true;
  let timer;
  let timeoutError;
  const getTimeoutError = () => {
    if (!timeoutError) {
      const pendingStep = typeof label === "function" ? label() : label;
      timeoutError = new Error(`${pendingStep} was not confirmed in time. Retry the affected sites after this action finishes.`);
      timeoutError.code = "mc/inventory-timeout";
    }
    return timeoutError;
  };
  const assertActive = () => {
    if (!active) throw getTimeoutError();
  };
  const work = Promise.resolve().then(() => operation(assertActive));
  return new Promise((resolve, reject) => {
    timer = setTimeout(() => {
      active = false;
      reject(getTimeoutError());
    }, timeoutMs);
    work.then(resolve, reject);
  }).finally(() => {
    active = false;
    clearTimeout(timer);
  });
}

export async function runInventorySiteDistribution(targets, worker, { onProgress = () => {}, onStage = () => {}, timeoutMs = 60000, concurrency = 4 } = {}) {
  const results = new Array(targets.length);
  let nextIndex = 0;
  let completed = 0;
  const run = async () => {
    while (nextIndex < targets.length) {
      const index = nextIndex++;
      const target = targets[index];
      let stage = "Starting site distribution";
      const reportStage = (nextStage) => {
        stage = String(nextStage || stage);
        onStage({ site: target.label || target.site, stage, current: completed, total: targets.length });
      };
      let result;
      try {
        result = await withInventoryDeadline(
          (assertActive) => worker(target, assertActive, (nextStage) => { assertActive(); reportStage(nextStage); }),
          () => `Distribution for ${target.label || target.site} · ${stage}`,
          timeoutMs,
        );
        if (!result) throw new Error("Site distribution returned no result.");
      } catch (error) {
        result = { site: target.site, label: target.label || target.site, ok: false, skipped: false, status: "failed", reason: error?.message || "Site distribution failed" };
      }
      results[index] = result;
      completed += 1;
      onProgress({ current: completed, total: targets.length, site: result.label || result.site, results: results.filter(Boolean) });
      // Let progress paint between CPU-heavy snapshot/revision calculations.
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  };
  const workers = Math.min(targets.length, Math.max(1, Math.floor(concurrency) || 1));
  await Promise.all(Array.from({ length: workers }, run));
  return results;
}
